<template>
  <div class="engagement-overview" data-qid="engagement-overview">

    <div class="v2-grid">
    <div class="v2-col">
    <!-- Needs attention: one row per thing to unblock, with a way to act on it -->
    <div class="v2-card" data-qid="engagement-needs-attention">
      <div class="v2-card__head">
        <h3>{{ $t('projects.engagement.overview.needs_attention') }}</h3>
      </div>
      <div v-for="(item, index) in attentionItems"
           :key="'attention-' + index"
           class="v2-att"
      >
        <span class="v2-att__icon" :class="'v2-att__icon--' + (item.tone || 'blue')"><font-awesome-icon :icon="item.icon"/></span>
        <span class="v2-att__text">{{ item.text }}</span>
        <span v-if="item.tab"
              class="btn btn-secondary btn-sm"
              @click="$emit('open-tab', item.tab)"
              :data-qid="'engagement-attention-open-' + index"
        >{{ $t('projects.engagement.overview.open_tab', { tab: $t('projects.engagement.tab.' + item.tab) }) }}</span>
      </div>
      <div v-if="!attentionItems.length" class="v2-att">
        <span class="v2-att__icon v2-att__icon--teal"><font-awesome-icon icon="check"/></span>
        <span class="v2-att__text text-muted">{{ $t('projects.engagement.overview.nothing_needs_attention') }}</span>
      </div>
    </div>

    <!-- Steps of the selected phase -->
    <div class="panel engagement-overview__phase">
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="tasks" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('projects.engagement.phase_number', { number: phases.indexOf(selectedPhase) + 1 }) }} · {{ $t('projects.engagement.phase.' + selectedPhase) }}
                <span class="section-compact__secondary" v-if="selectedPhase === 'execution' && engagement.task_count">
                  {{ $t('projects.engagement.overview.task_progress', { done: engagement.completed_task_count, total: engagement.task_count }) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="profile-compact__actions" v-if="phaseTab(selectedPhase)">
          <a href="#" @click.prevent="$emit('open-tab', phaseTab(selectedPhase))" data-qid="engagement-open-phase-tab">
            {{ $t('projects.engagement.overview.open_tab', { tab: $t('projects.engagement.tab.' + phaseTab(selectedPhase)) }) }}
            <font-awesome-icon icon="external-link-alt" class="ml-1"/>
          </a>
        </div>
      </header>

      <div class="panel__body">
        <!-- Switch between the phases; the current phase is selected first -->
        <div class="v2-seg mb-3" data-qid="engagement-phase-switch">
          <span v-for="phase in phases"
                :key="'switch-' + phase"
                :class="{'on': selectedPhase === phase}"
                @click="selectedPhase = phase"
                :data-qid="'engagement-phase-switch-' + phase"
          >
            <font-awesome-icon :icon="phaseIcon(phase)" class="mr-1"/>{{ $t('projects.engagement.phase_short.' + phase) }}
          </span>
        </div>
        <EngagementLockedNotice v-if="selectedPhase !== 'acceptance' && !engagement.unlocked"></EngagementLockedNotice>


        <EngagementStep v-for="step in phaseSteps(selectedPhase)"
                        :key="'overview-step-' + step.step"
                        :project="project"
                        :step="step"
                        :editable="canEditPhase(selectedPhase)"
                        :showDescription="true"
                        :review="engagement.review"
                        :evidence="engagement.evidence"
                        @changed="$emit('changed', $event)"
                        @media-changed="$emit('media-changed')"
                        @open-tab="$emit('open-tab', $event)"
        ></EngagementStep>
      </div>
    </div>

    <!-- Tasks per control objective -->
    <div class="v2-card" v-if="objectives.length" data-qid="engagement-objectives">
      <div class="v2-card__head">
        <h3>{{ $t('projects.engagement.overview.objectives_title') }}</h3>
        <span class="v2-card__sub">{{ $t('projects.engagement.overview.objectives_hint') }}</span>
      </div>
      <div class="v2-legend">
        <span v-for="bucket in buckets" :key="'legend-' + bucket"><i :class="'v2-b--' + bucket"></i>{{ $t('projects.engagement.overview.bucket.' + bucket) }}</span>
      </div>
      <div class="v2-objectives">
        <div v-for="objective in objectives"
             :key="'objective-' + objective.id"
             class="v2-objective"
             @click="$emit('open-tab', 'tasks')"
             :data-qid="'engagement-objective-' + objective.id"
        >
          <span class="v2-objective__name text-truncate" :title="objective.name">{{ objective.name }}</span>
          <span class="v2-stack">
            <i v-for="bucket in bucketsOf(objective)"
               :key="objective.id + '-' + bucket"
               :class="'v2-b--' + bucket"
               :style="{ width: (objective[bucket] / objective.total * 100) + '%' }"
               v-b-tooltip.hover
               :title="$t('projects.engagement.overview.bucket.' + bucket) + ': ' + objective[bucket]"
            ></i>
          </span>
          <span class="v2-objective__value">{{ objective.signed_off }} / {{ objective.total }}</span>
        </div>
      </div>
    </div>
    </div>

    <!-- Right column: schedule, budget, team and activity (filled by the project page) -->
    <div class="v2-col">
      <slot name="aside"></slot>
    </div>
    </div>
  </div>
</template>

<script>
import EngagementStep from '@/views/Projects/Engagement/EngagementStep.vue';
import EngagementLockedNotice from '@/views/Projects/Engagement/EngagementLockedNotice.vue';
import { PHASE_KEYS, canEditPhase, engagementPermissions, formatHours, budgetEnabled, phaseSteps, phaseIncluded, phaseCompleted, currentPhase, phaseState } from '@/views/Projects/Engagement/engagement.js';

export default {
  name: 'EngagementOverview',
  props: {
    project: {
      type: Object,
      required: true
    },
    engagement: {
      type: Object,
      required: true
    }
  },
  components: {
    EngagementStep,
    EngagementLockedNotice
  },
  data: function() {
    return {
      phases: PHASE_KEYS,
      selectedPhase: null,
      buckets: ['signed_off', 'awaiting_qa', 'ready_to_test', 'waiting_on_client', 'in_progress']
    }
  },
  computed: {
    access() {
      return engagementPermissions(this.project);
    },
    currentPhase() {
      return currentPhase(this.engagement);
    },
    objectives() {
      return this.engagement.objectives || [];
    },
    attentionItems() {
      let items = [];
      const engagement = this.engagement;

      // Work waiting in the tasks: rejected in QA first, then tasks the client has delivered
      const review = engagement.review || {};
      if (review.rejected) {
        items.push({ icon: 'times', tone: 'red', text: this.$tc('projects.engagement.attention.rejected_tasks', review.rejected, { count: review.rejected }), tab: 'tasks' });
      }
      const readyToTest = _.sumBy(engagement.objectives || [], 'ready_to_test');
      if (readyToTest) {
        items.push({ icon: 'check', tone: 'violet', text: this.$tc('projects.engagement.attention.ready_to_test', readyToTest, { count: readyToTest }), tab: 'tasks' });
      }

      if (engagement.requires_acceptance && !engagement.accepted_at) {
        items.push({ icon: 'file-signature', tone: 'amber', text: this.$t('projects.engagement.attention.accept_engagement'), tab: 'acceptance' });
      }

      if (this.project.include_independence_declaration) {
        const statuses = this.project.users_independence || {};
        const pending = _.filter(this.project.users || [], user => statuses[user.id] !== 1).length;
        if (pending) {
          items.push({ icon: 'user-check', tone: 'amber', text: this.$t('projects.engagement.attention.independence_pending', { count: pending }) });
        }
      }

      const roles = this.project.users_roles || {};
      if ((this.project.users || []).length && !_.some(this.project.users, user => roles[user.id] === 'manager')) {
        items.push({ icon: 'user-cog', tone: 'red', text: this.$t('projects.engagement.attention.no_manager') });
      }

      if (engagement.evidence && engagement.evidence.unlinked) {
        items.push({ icon: 'paperclip', tone: 'amber', text: this.$t('projects.engagement.attention.unlinked_evidence', { count: engagement.evidence.unlinked }), tab: 'documents' });
      }

      if (engagement.unlocked && engagement.unassigned_task_count) {
        items.push({ icon: 'user-tag', tone: 'blue', text: this.$t('projects.engagement.attention.unassigned_tasks', { count: engagement.unassigned_task_count }), tab: 'tasks' });
      }

      _.forEach(this.phases, phase => {
        const budget = engagement.budget ? engagement.budget[phase] : 0;
        const spent = engagement.spent ? engagement.spent[phase] : 0;
        if (budgetEnabled(this.project) && budget > 0 && spent > budget) {
          items.push({
            icon: 'hourglass-half',
            tone: 'red',
            text: this.$t('projects.engagement.attention.over_budget', {
              phase: this.$t('projects.engagement.phase_short.' + phase),
              spent: formatHours(spent),
              budget: formatHours(budget)
            }),
            tab: 'planning'
          });
        }
      });

      // Associates don't have the Manager tabs, so those items don't link anywhere for them
      return _.map(items, item => (item.tab && !this.tabAvailable(item.tab) ? Object.assign({}, item, { tab: null }) : item));
    }
  },
  watch: {
    currentPhase: {
      immediate: true,
      handler(phase) {
        if (!this.selectedPhase) {
          this.selectedPhase = phase;
        }
      }
    }
  },
  methods: {
    // Buckets with at least one task, so empty segments aren't drawn
    bucketsOf(objective) {
      return _.filter(this.buckets, bucket => objective[bucket] > 0);
    },
    canEditPhase(phase) {
      return canEditPhase(this.project, phase);
    },
    phaseSteps(phase) {
      return phaseSteps(this.engagement, phase);
    },
    phaseIncluded(phase) {
      return phaseIncluded(this.engagement, phase);
    },
    phaseCompleted(phase) {
      return phaseCompleted(this.engagement, phase);
    },
    phaseState(phase) {
      return phaseState(this.engagement, phase);
    },
    phaseIcon(phase) {
      const icons = {
        completed: 'check-circle',
        current: 'adjust',
        open: 'circle',
        locked: 'lock',
        skipped: 'minus-circle'
      };
      return icons[this.phaseState(phase)];
    },
    phaseTab(phase) {
      const tabs = { acceptance: 'acceptance', planning: 'planning', execution: 'tasks', reporting: 'reporting' };
      return this.tabAvailable(tabs[phase]) ? tabs[phase] : null;
    },
    // Engagement acceptance, Planning and Settings are Manager tabs
    tabAvailable(tab) {
      return !['acceptance', 'planning', 'settings'].includes(tab) || !!this.access.manage;
    }
  }
}
</script>
